import type { MaterialIssueOut } from '@/features/storekeeping/types'
import { customFetch } from '@/lib/api/fetcher'
import { queryOptions } from '@tanstack/react-query'

/**
 * Per-WO list factories for MIN / PR / GR documents.
 *
 * Backend contracts (ADR-0005 — global URLs only):
 *   GET /api/material-issues?work_order_id={woid}    → MaterialIssueOut[]
 *   GET /api/production-reports?work_order_id={woid} → ProductionReportOut[]
 *   GET /api/warehouse/goods-receipts/?work_order_id={woid} → GoodsReceiptForWoOut[]
 *
 * `projectId` is retained in queryKey for cache invalidation symmetry with
 * project-scoped views; it is NOT sent to the backend as a filter (the
 * `work_order_id` filter is sufficient — WO pins down the project).
 *
 * Hand-rolled until orval regenerates after ADR-0005 ships. Match
 * `apps.workorders.schemas.production_report` 1:1.
 */

export interface ProductionReportLineOut {
  id: number
  task_line_id: number
  produced_qty: number
  hours_spent: string
  material_consumed_qty: number
  consumed_from_lot_id: number | null
  notes: string
}

export interface ProductionReportOut {
  id: number
  number: string
  project_id: number | null
  work_order_id: number | null
  shift_date: string
  created_by_id: number
  status: string
  posted_at: string | null
  reversal_of_id: number | null
  goods_receipt_id: number | null
  notes: string
  created_at: string
  lines: ProductionReportLineOut[]
}

export const woMaterialIssuesQuery = (projectId: number, workOrderId: number) =>
  queryOptions({
    queryKey: ['phase12', 'material-issues', projectId, workOrderId] as const,
    queryFn: ({ signal }) =>
      customFetch<MaterialIssueOut[]>(`/api/material-issues?work_order_id=${workOrderId}`, {
        signal,
      }),
    staleTime: 15_000,
  })

export const woProductionReportsQuery = (projectId: number, workOrderId: number) =>
  queryOptions({
    queryKey: ['phase12', 'reports', projectId, workOrderId] as const,
    queryFn: ({ signal }) =>
      customFetch<ProductionReportOut[]>(`/api/production-reports?work_order_id=${workOrderId}`, {
        signal,
      }),
    staleTime: 15_000,
  })

export interface GoodsReceiptForWoOut {
  id: number
  number: string
  receipt_date: string
  project_id: number | null
  work_order_id: number | null
  storekeeper_id: number
  status: string
  posted_at: string | null
  notes: string
  created_by_id: number
  created_at: string
  lines: ReadonlyArray<{ id: number; qty: number }>
}

export const woGoodsReceiptsQuery = (workOrderId: number) =>
  queryOptions({
    queryKey: ['phase13', 'goods-receipts', 'by-wo', workOrderId] as const,
    queryFn: ({ signal }) =>
      customFetch<GoodsReceiptForWoOut[]>(
        `/api/warehouse/goods-receipts/?work_order_id=${workOrderId}`,
        { signal },
      ),
    staleTime: 15_000,
  })
